import { useId, useRef, useState } from 'react'
import styles from '../../boardui/Navigation.module.css'

export function Tabs({ items = [], value, defaultValue, onValueChange, label = 'Sections', className = '', keepMounted = false }) {
  const generatedId = useId()
  const normalizedItems = items.map((item) => ({ ...item, value: item.value ?? item.key }))
  const [internalValue, setInternalValue] = useState(defaultValue ?? normalizedItems[0]?.value)
  const selectedValue = value ?? internalValue
  const tabRefs = useRef(new Map())
  const [mountedValues, setMountedValues] = useState(() => new Set(selectedValue == null ? [] : [selectedValue]))

  const select = (nextValue) => {
    if (keepMounted) {
      setMountedValues((current) => {
        if (current.has(selectedValue) && current.has(nextValue)) return current
        return new Set([...current, selectedValue, nextValue])
      })
    }
    if (value === undefined) setInternalValue(nextValue)
    onValueChange?.(nextValue)
  }

  const moveFocus = (currentValue, direction) => {
    const enabled = normalizedItems.filter((item) => !item.disabled)
    const current = enabled.findIndex((item) => item.value === currentValue)
    const next = enabled[(current + direction + enabled.length) % enabled.length]
    tabRefs.current.get(next?.value)?.focus()
    if (next) select(next.value)
  }

  return (
    <div className={`${styles.tabs} ${className}`.trim()}>
      <div role="tablist" aria-label={label} className={styles.tabList}>
        {normalizedItems.map((item) => {
          const selected = item.value === selectedValue
          const tabId = `${generatedId}-tab-${item.value}`
          const panelId = `${generatedId}-panel-${item.value}`
          return (
            <button
              key={item.value}
              ref={(node) => node ? tabRefs.current.set(item.value, node) : tabRefs.current.delete(item.value)}
              id={tabId}
              type="button"
              role="tab"
              className={styles.tab}
              aria-selected={selected}
              aria-controls={panelId}
              tabIndex={selected ? 0 : -1}
              disabled={item.disabled}
              onClick={() => select(item.value)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowRight') { event.preventDefault(); moveFocus(item.value, 1) }
                if (event.key === 'ArrowLeft') { event.preventDefault(); moveFocus(item.value, -1) }
                if (event.key === 'Home') {
                  event.preventDefault()
                  const first = normalizedItems.find((entry) => !entry.disabled)
                  tabRefs.current.get(first?.value)?.focus()
                  if (first) select(first.value)
                }
                if (event.key === 'End') {
                  event.preventDefault()
                  const last = [...normalizedItems].reverse().find((entry) => !entry.disabled)
                  tabRefs.current.get(last?.value)?.focus()
                  if (last) select(last.value)
                }
              }}
            >
              {item.label}{item.count != null ? (
                // A hidden comma and a real space keep the name readable:
                // "Player markets, 3", not "Player markets3".
                <><span className="wf-visually-hidden">,</span> <span className={styles.count}>{item.count}</span></>
              ) : null}
            </button>
          )
        })}
      </div>
      {normalizedItems.map((item) => {
        const selected = item.value === selectedValue
        return (
          <div key={item.value} id={`${generatedId}-panel-${item.value}`} role="tabpanel" aria-labelledby={`${generatedId}-tab-${item.value}`} className={styles.panel} hidden={!selected} tabIndex={0}>
            {selected || (keepMounted && mountedValues.has(item.value)) ? item.panel : null}
          </div>
        )
      })}
    </div>
  )
}
